import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClients } from 'src/api';
import { Theme, ThemeContext } from 'src/hooks';
import { texts } from 'src/texts';

const defaultTheme: { name: string | null } = { name: texts.common.appName };

export interface ThemeProviderProps extends React.PropsWithChildren {}

export const ThemeProvider = (props: ThemeProviderProps) => {
  const { children } = props;
  const clients = useClients();

  const {
    data: loadedSettings,
    error,
    refetch,
  } = useQuery({
    queryKey: ['theme'],
    queryFn: () => clients.settings.getSettings(),
  });

  const [customTheme, setCustomTheme] = useState<Theme>({} as any);

  const initialTheme = useMemo(() => {
    return merge(defaultTheme, loadedSettings);
  }, [loadedSettings]);

  const theme = useMemo(() => {
    return merge(initialTheme, customTheme) as Theme;
  }, [customTheme, initialTheme]);

  const setTheme = useCallback((values: Partial<Theme>) => {
    setCustomTheme((v) => ({ ...v, ...values }));
  }, []);

  const context = useMemo(() => {
    return {
      refetch,
      theme,
      setTheme,
    };
  }, [refetch, setTheme, theme]);

  useEffect(() => {
    if (error) {
      console.error(error);
    }
  }, [error]);

  if (!loadedSettings) {
    return null;
  }

  return <ThemeContext.Provider value={context}>{children}</ThemeContext.Provider>;
};

function merge<T extends object>(target: T, source?: Partial<T>): T {
  const result = { ...target };

  if (source) {
    for (const [key, value] of Object.entries(source || {}).filter((x) => !!x[1])) {
      (result as any)[key] = value;
    }
  }

  return result;
}
